import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { pageBgColor } from "@/utils/theme";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Add } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
	InspectionDetail,
	InspectionReading,
	InspectionValue,
} from "../models/Inspection";
import {
	deleteInspectionRow,
	getInspection,
	upsertInspectionRow,
} from "../repositories/InspectionRepository";
import { InspectionSheetHeader } from "./components/InspectionSheetHeader";
import { InspectionSheetTable } from "./components/InspectionSheetTable";
import {
	buildReadingDraft,
	ReadingDraft,
	toInspectionRowDraft,
} from "../models/Reading";

export type SheetLastUpdate = {
	at: Date;
	by: string | null;
	rowId: number;
};

export const buildLastUpdate = (
	readings: InspectionReading[],
): SheetLastUpdate | null => {
	let last: InspectionReading | null = null;

	for (const reading of readings) {
		if (!reading.UpdatedAt) continue;

		if (!last?.UpdatedAt || reading.UpdatedAt > last.UpdatedAt) last = reading;
	}

	if (!last?.UpdatedAt) return null;

	return { at: last.UpdatedAt, by: last.UpdatedBy, rowId: last.RowId };
};

export const buildReadings = (
	detail: InspectionDetail | undefined,
): InspectionReading[] => {
	if (!detail) return [];

	return detail.rows.map((row) => {
		const valueByColumnId: Record<number, InspectionValue> = {};
		for (const value of detail.values) {
			if (value.RowId === row.RowId) valueByColumnId[value.ColumnId] = value;
		}

		return {
			...row,
			ValueByColumnId: valueByColumnId,
			Defects: detail.defects.filter((defect) => defect.RowId === row.RowId),
		};
	});
};

type UseInspectionProps = {
	inspectionId: number;
	onError: (error: Error) => void;
};

export const useInspection = ({ inspectionId, onError }: UseInspectionProps) =>
	useQuery<InspectionDetail, Error>({
		queryKey: ["inspection", inspectionId],
		queryFn: ({ signal }) => getInspection({ inspectionId }, signal),
		enabled: !!inspectionId,
		onError,
	});

export const InspectionSheetPage = () => {
	const { inspectionId } = useParams();
	const queryClient = useQueryClient();

	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [draft, setDraft] = useState<ReadingDraft | null>(null);
	const [originalDraft, setOriginalDraft] = useState<ReadingDraft | null>(null);
	const [confirmDiscard, setConfirmDiscard] = useState(false);
	const [editingRowId, setEditingRowId] = useState<number | null>(null);
	const [readingToDelete, setReadingToDelete] =
		useState<InspectionReading | null>(null);
	const [isPointingAtLastUpdate, setIsPointingAtLastUpdate] = useState(false);

	const { data, isLoading } = useInspection({
		inspectionId: Number(inspectionId),
		onError: (e) => setError(e.message),
	});

	const inspection = data?.inspection ?? null;
	const columns = data?.columns ?? [];
	const readings = buildReadings(data);
	const lastUpdate = buildLastUpdate(readings);

	const refresh = async () => {
		await queryClient.invalidateQueries({
			queryKey: ["inspection", Number(inspectionId)],
		});
		await queryClient.invalidateQueries({ queryKey: ["inspections"] });
	};

	const upsertMutation = useMutation(upsertInspectionRow, {
		onSuccess: async () => {
			await refresh();
			setNotificationMessage("The reading was saved succesfully");
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const deleteMutation = useMutation(deleteInspectionRow, {
		onSuccess: async () => {
			await refresh();
			setNotificationMessage("The reading was deleted succesfully");
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const isMutating = upsertMutation.isPending || deleteMutation.isPending;

	const cancelEdit = () => {
		setEditingRowId(null);
		setDraft(null);
		setOriginalDraft(null);
		setConfirmDiscard(false);
	};

	const requestCancelEdit = () => {
		if (
			draft &&
			originalDraft &&
			JSON.stringify(draft) !== JSON.stringify(originalDraft)
		) {
			setConfirmDiscard(true);
			return;
		}

		cancelEdit();
	};

	const isEditable = inspection?.IsEditable ?? false;

	const startAdd = () => {
		const newDraft = buildReadingDraft(columns, null);

		setEditingRowId(null);
		setDraft(newDraft);
		setOriginalDraft(newDraft);
	};

	const startEdit = (reading: InspectionReading) => {
		const editDraft = buildReadingDraft(columns, reading);

		setEditingRowId(reading.RowId);
		setDraft(editDraft);
		setOriginalDraft(editDraft);
	};

	const saveDraft = (readingDraft: ReadingDraft) => {
		upsertMutation.mutate(
			toInspectionRowDraft(readingDraft, Number(inspectionId), columns),
		);
		cancelEdit();
	};

	return (
		<>
			<HelmetDexteel
				title={
					inspection
						? `${inspection.SectionName} - Inspection Sheet`
						: "Inspection Sheet"
				}
			/>
			<Box
				sx={{
					backgroundColor: pageBgColor,
					height: "calc(100vh - 70px)",
					display: "flex",
					flexDirection: "column",
					gap: 1,
					p: { xs: 1, md: 2 },
				}}
			>
				<InspectionSheetHeader
					inspection={inspection}
					lastUpdate={lastUpdate}
					onError={setError}
					onPointAtLastUpdate={setIsPointingAtLastUpdate}
				/>

				<Box sx={{ flex: 1, minHeight: 0 }}>
					<InspectionSheetTable
						columns={columns}
						sectionId={inspection?.SectionId ?? null}
						readings={readings}
						draft={draft}
						editingRowId={editingRowId}
						isLoading={isLoading}
						isMutating={isMutating}
						readOnly={!isEditable}
						highlight={isPointingAtLastUpdate ? lastUpdate : null}
						onStartEdit={startEdit}
						onCancelEdit={requestCancelEdit}
						onDraftChange={setDraft}
						onSaveDraft={saveDraft}
						onDelete={(reading) => setReadingToDelete(reading)}
					/>
				</Box>

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-end",
						gap: 1,
					}}
				>
					{inspection && !isEditable && (
						<Typography variant="body2" color="text.secondary">
							This sheet is closed: its inspector has already started a sheet of
							another section.
						</Typography>
					)}
					<Button
						variant="contained"
						startIcon={<Add />}
						onClick={startAdd}
						disabled={!isEditable || draft !== null || isMutating}
					>
						Add Reading
					</Button>
				</Box>
			</Box>

			<ConfirmationDialogRaw
				title="Discard changes"
				message="This reading has unsaved changes. Discard them?"
				open={confirmDiscard}
				buttons={{ no: "Keep editing", yes: "Discard" }}
				onClose={(value) => {
					if (value === "OK") cancelEdit();
					else setConfirmDiscard(false);
				}}
			/>
			<ConfirmationDialogRaw
				title="Delete reading"
				message={
					readingToDelete
						? "The reading and everything read on it will be deleted. Continue?"
						: ""
				}
				open={readingToDelete !== null}
				buttons={{ no: "Cancel", yes: "Delete" }}
				onClose={(value) => {
					if (value === "OK" && readingToDelete)
						deleteMutation.mutate(readingToDelete.RowId);
					setReadingToDelete(null);
				}}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
